import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';

import { AddAddonModalComponent } from '@shared/components/modals/add-addon-modal/add-addon-modal.component';
import { PluginInformationModalComponent } from '@shared/components/modals/plugin-information-modal/plugin-information-modal.component';

import { UserService } from '@core/services/user.service';
import { AddonService } from '@shared/services/addon.service';
import { CategoryService } from '@shared/services/category.service';
import { CustomEventService } from '@shared/services/custom-event.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { FREE_ADDONS } from '@shared/variables/constant-variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService  } from 'ng-zorro-antd/notification';
import { getUserLimitations } from "@shared/utils/limitation.util";

@UntilDestroy()
@Component({
  selector: 'app-market-place',
  templateUrl: './market-place.component.html'
})
export class MarketPlaceComponent implements OnInit, OnDestroy {

  DESCRIPTION_LENGTH = 65;
  freeAddons = FREE_ADDONS;

  searchValue: string;
  categories: Addon.Category[];
  addons: Addon.AddonModel[];
  addOnGroups: Addon.AddonGroup[];
  loading: boolean;
  modal: NzModalRef;
  addOnDetailLoading = false;

  activeUser: ActiveUser.ActiveUserModel;

  @ViewChild('notificationTemp', { static: true })
  notificationTemp: TemplateRef<any>;
  notificationDescription: string;

  customEventSubscription: Subscription;
  addAddonModal: NzModalRef;
  licenseLimitations: any;
  licenseFreeAddons: any;

  constructor(
    private addonService: AddonService,
    private categoryService: CategoryService,
    private customEventService: CustomEventService,
    private modalService: NzModalService,
    private notificationService: NzNotificationService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.activeUser = this.userService.activeUser;

    this.loadInitialData();

    getUserLimitations(this.userService).then(response => {
      this.licenseLimitations = response;
      this.licenseFreeAddons = response.freeAddons;

    }).catch(error => {
      console.log('error while limitations data', error);
    });

    this.customEventSubscription = this.customEventService.eventAnnounced$
      .pipe(untilDestroyed(this)).subscribe(
      ({ action, data }) => {
        if (action === ActionTypesEnum.CLOSE_MODAL) {
          if (this.addAddonModal) {
            this.addAddonModal.close();
          }
        }

        if (action === ActionTypesEnum.INSTALL_ADDON) {
          this.installHandler(data);
        }

        if (action === ActionTypesEnum.CREATE_ADDON) {
          this.createAddon(data);
        }
      });
  }

  loadInitialData(): void {
    forkJoin({
      addons: this.addonService.list({}),
      categories: this.categoryService.list()
    }).pipe(untilDestroyed(this))
      .subscribe(({addons, categories}: {addons: Addon.AddonModel[], categories: Addon.Category[]}) => {
        this.addons = addons.map(item => {
          return {
            ...item,
            free: this.freeAddons.includes(item.name)
          }
        });
        this.categories = categories;
        this.loading = false;

        this.addOnGroups = this.categories;
        this.addOnGroups = this.addOnGroups.map(category => {
          category.addons = this.addons.filter(addon => addon.category.id === category.id);
          return category;
        });
      }, error => {
        this.loading = false;
        console.log('error occurred while fetching initial data for marketplace', error);
      });
  }

  expandGroup(group: Addon.AddonGroup): void {
    group.isExpanded = true;
  }

  installHandler(addOn: Addon.AddonModel): void {
    const innerComponent = this.modal.getContentComponent();

    innerComponent.loading = true;

    this.addonService.activate(addOn.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {

        this.notificationDescription = `${addOn.name} add-on is successfully added to DataSources.`;
        this.addonService.list({}).subscribe(_ => {
        }, error => {
          console.log('error occurred while fetching initial data for marketplace', error);
        });
        innerComponent.loading = false;

        setTimeout((): void => {
          this.modal.destroy();
          this.notificationService.template(this.notificationTemp);

        }, 500);
      }, err => {
        if(err.error.message) {
          this.modal.destroy();
        }
        console.log('error while fetching install addon', err);
      });
  }

  viewAddOnDetail(id: string): void {
    if (this.addOnDetailLoading) {
      return;
    }
    this.addOnDetailLoading = true;
    this.addonService.get(id)
      .pipe(untilDestroyed(this))
      .subscribe((addOn) => {
        addOn = {
          ...addOn,
          free: this.freeAddons.includes(addOn.name)
        };
        this.modal = this.modalService.create({ 
          nzMaskClosable: false,
          nzContent: PluginInformationModalComponent,
          nzComponentParams: {
            addOn,
            activeUser: this.userService.activeUser,
            licenseFreeAddons: this.licenseFreeAddons
          },
          nzWidth: '60%',
          nzFooter: null
        });
        this.addOnDetailLoading = false;
      }, error => {
        this.addOnDetailLoading = false;
        console.log('error occurred while fetching add-on detail for marketplace', error);
      });
  }

  showAddonModal(): void {
    this.addAddonModal = this.modalService.create({
      nzMaskClosable: false,
      nzContent: AddAddonModalComponent,
      nzWidth: '450px',
      nzFooter: null,
      nzComponentParams: {
        categories: this.categories
      }
    });
  }

  createAddon(data): void {
    const innerComponent = this.addAddonModal.getContentComponent();

    innerComponent.loading = true;

    this.addonService.createAddon(data)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        this.addAddonModal.close();
        this.loadInitialData();

        innerComponent.loading = false;
      }, error => {
        innerComponent.loading = false;
        console.log(`error occurred while create custom addon`, error);
      });
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
    this.customEventSubscription.unsubscribe();
  }
}
